import { chain } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';

import type { MyScoutItem } from '@appTypes/esb';
import type { UserOrganizationInfo } from '@modules/user/duck/types';
import { programForUnitType } from '@shared/constants';
import type { UnitTypeName } from '@shared/constants';
import normalizeUnitGender from '@shared/utils/normalizeUnitGender';
import { getPreferredName } from '@shared/utils/preferredName';

import type { ChildrenOrg } from './duck/types';

export const removeParentGuardiansFromOrganizations = (
  orgs: UserOrganizationInfo[],
) => orgs.filter(({ isParentGuardian }) => !isParentGuardian);

export const removeEmptyOrganizations = (
  organizations: UserOrganizationInfo[],
) => {
  const keys: Array<keyof UserOrganizationInfo> = [
    'organizationGuid',
    'organizationName',
    'personGuid',
  ];

  const filtered = organizations.filter(orgInfo => {
    const minimumRequiredFields = keys.map(key => orgInfo[key]);

    if (minimumRequiredFields.every(isEmpty)) return false;

    return true;
  });

  return filtered;
};

const getOrgPositionsFromChildren = (
  children: MyScoutItem[],
  currentChild: MyScoutItem,
) =>
  chain(children)
    .filter(
      item =>
        item.orgGuid === currentChild.orgGuid &&
        item.userId === currentChild.userId,
    )
    .uniqBy('positionId')
    .map(pos => ({ id: Number(pos.positionId), position: pos.position }))
    .value();

export function getOrganizationsFromChildren(params: {
  myScout: MyScoutItem[];
}): Array<ChildrenOrg> {
  if (!params.myScout) return [];

  const result = uniqBy(
    params.myScout.map(item => {
      const childrenOrg: ChildrenOrg = {
        ...item,
        userId: Number(item.userId),
        isParentGuardian: true,
        /** Name of the child */
        connection: getPreferredName(item),
        key: `${item.orgGuid}-${item.position}-${item.userId}`,
        organizationGuid: `${item.orgGuid}*${item.userId}`,
        acceptGender: item.acceptGender,
        unitGender: normalizeUnitGender(item.acceptGender),
        organizationName: `${item.unitType} ${item.unitNumber}`,
        programType: item.program,
        program: programForUnitType[item.unitType as UnitTypeName],
        programId: item.programId,
        unitTypeId: item.unitTypeId,
        organizationPositions: [
          {
            organizationGuid: item.orgGuid,
            positions: getOrgPositionsFromChildren(params.myScout, item),
          },
        ],
      };

      return childrenOrg;
    }),
    'organizationGuid',
  );

  return result;
}

/** This is generated for org info from children when user is parent */
export type ChildrenOrgInfo = ReturnType<
  typeof getOrganizationsFromChildren
>[number];
